import type { Compactor, Layout, LayoutItem } from 'react-grid-layout'
import { verticalCompactor } from 'react-grid-layout/core'

import type { DashboardGridChange, Widget } from '../types'
import {
  DASHBOARD_GRID_COLUMNS,
  DASHBOARD_WIDGET_GAP_COLUMNS,
  DASHBOARD_WIDGET_GAP_ROWS,
} from './dashboardGrid.constants'

export const dashboardGridCompactor: Compactor = {
  type: 'vertical',
  allowOverlap: false,
  compact(layout, columns) {
    const layoutById = new Map(layout.map((item) => [item.i, item]))
    const layoutWithGaps = layout.map((item) => ({
      ...item,
      w: item.w + DASHBOARD_WIDGET_GAP_COLUMNS,
      h: item.h + DASHBOARD_WIDGET_GAP_ROWS,
    }))

    const compactedLayout = verticalCompactor.compact(
      layoutWithGaps,
      columns + DASHBOARD_WIDGET_GAP_COLUMNS
    )

    return compactedLayout.map((item) => {
      const sourceItem = layoutById.get(item.i)

      return sourceItem ? { ...item, w: sourceItem.w, h: sourceItem.h } : item
    })
  },
}

export function compactWidgetLayouts(widgets: Widget[]): Widget[] {
  const layout = widgets.flatMap((widget) => (widget.layout ? [widget.layout] : []))
  const compactedLayouts = new Map(
    dashboardGridCompactor
      .compact(layout, DASHBOARD_GRID_COLUMNS)
      .map((layoutItem) => [layoutItem.i, layoutItem])
  )

  return widgets.map((widget) => {
    const compactedLayout = compactedLayouts.get(widget.id)

    return compactedLayout ? { ...widget, layout: compactedLayout } : widget
  })
}

export function getMaxResizeWidth(
  item: LayoutItem,
  layout: Layout,
  direction: 'left' | 'right'
): number {
  const itemRight = item.x + item.w
  let maxWidth = item.maxW ?? Infinity

  for (const otherItem of layout) {
    if (otherItem.i === item.i || !overlapsVertically(item, otherItem)) continue

    if (direction === 'right' && otherItem.x >= itemRight) {
      maxWidth = Math.min(maxWidth, otherItem.x - item.x - DASHBOARD_WIDGET_GAP_COLUMNS)
      continue
    }

    const otherItemRight = otherItem.x + otherItem.w

    if (direction === 'left' && otherItemRight <= item.x) {
      maxWidth = Math.min(maxWidth, itemRight - otherItemRight - DASHBOARD_WIDGET_GAP_COLUMNS)
    }
  }

  return Math.max(item.w, maxWidth)
}

export function getPersistedLayoutItem(
  nextLayout: LayoutItem,
  previousLayout: LayoutItem | null | undefined,
  change: Pick<DashboardGridChange, 'changedItem' | 'interaction'>
): LayoutItem {
  const isResizedWidget = change.interaction === 'resize' && nextLayout.i === change.changedItem.i

  return {
    ...nextLayout,
    w: isResizedWidget ? change.changedItem.w : previousLayout?.w ?? nextLayout.w,
    h: isResizedWidget ? change.changedItem.h : previousLayout?.h ?? nextLayout.h,
  }
}

export function findFirstAvailableGridPosition(
  layout: Layout,
  size: Pick<LayoutItem, 'w' | 'h'>,
  columns: number
): Pick<LayoutItem, 'x' | 'y'> {
  const maxX = Math.max(0, columns - size.w)
  const layoutBottom = Math.max(
    0,
    ...layout.map((item) => item.y + item.h + DASHBOARD_WIDGET_GAP_ROWS)
  )

  for (let y = 0; y <= layoutBottom; y++) {
    for (let x = 0; x <= maxX; x++) {
      const candidate = { x, y, ...size }

      if (layout.every((item) => !collidesWithWidgetGap(candidate, item))) {
        return { x, y }
      }
    }
  }

  return { x: 0, y: layoutBottom }
}

function collidesWithWidgetGap(
  first: Pick<LayoutItem, 'x' | 'y' | 'w' | 'h'>,
  second: Pick<LayoutItem, 'x' | 'y' | 'w' | 'h'>
): boolean {
  return !(
    first.x + first.w + DASHBOARD_WIDGET_GAP_COLUMNS <= second.x ||
    second.x + second.w + DASHBOARD_WIDGET_GAP_COLUMNS <= first.x ||
    first.y + first.h + DASHBOARD_WIDGET_GAP_ROWS <= second.y ||
    second.y + second.h + DASHBOARD_WIDGET_GAP_ROWS <= first.y
  )
}

function overlapsVertically(first: LayoutItem, second: LayoutItem): boolean {
  return !(
    first.y + first.h + DASHBOARD_WIDGET_GAP_ROWS <= second.y ||
    second.y + second.h + DASHBOARD_WIDGET_GAP_ROWS <= first.y
  )
}
